import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import { getCocinasConfig, setCocinasConfig } from './cocinasStorage';

const Cocinas = () => {
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const [rows, setRows] = useState(() => getCocinasConfig());

  useEffect(() => {
    setCocinasConfig(rows);
  }, [rows]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDeleteId, setRowToDeleteId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);

  const [formNombre, setFormNombre] = useState('');
  const [formCodigo, setFormCodigo] = useState('');
  const [formOrden, setFormOrden] = useState(1);
  const [formActiva, setFormActiva] = useState(true);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => Number(a.orden || 0) - Number(b.orden || 0)),
    [rows]
  );

  const totals = useMemo(() => {
    const activas = rows.filter((r) => r.activa).length;
    return { activas, total: rows.length };
  }, [rows]);

  const resetForm = () => {
    setFormNombre('');
    setFormCodigo('');
    setFormOrden(rows.length ? Math.max(...rows.map((r) => Number(r.orden || 0))) + 1 : 1);
    setFormActiva(true);
  };

  const openAddModal = () => {
    resetForm();
    setEditingRow(null);
    setIsAddModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setFormNombre(row.nombre || '');
    setFormCodigo(row.codigo || '');
    setFormOrden(Number(row.orden || 1));
    setFormActiva(!!row.activa);
    setIsEditModalOpen(true);
  };

  const upsertRow = () => {
    const nombre = String(formNombre || '').trim();
    if (!nombre) {
      setToast({ isOpen: true, message: 'Indique el nombre de la cocina', type: 'error' });
      return;
    }
    const codigo = String(formCodigo || '').trim().toUpperCase();
    const orden = Math.max(0, Number(formOrden || 0));

    if (editingRow) {
      const id = editingRow.id;
      setRows((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, nombre, codigo, orden, activa: !!formActiva } : r
        )
      );
      setIsEditModalOpen(false);
      setEditingRow(null);
      setToast({ isOpen: true, message: 'Cocina actualizada', type: 'success' });
      return;
    }

    setRows((prev) => [
      ...prev,
      {
        id: prev.length ? Math.max(...prev.map((x) => x.id)) + 1 : 1,
        nombre,
        codigo,
        orden,
        activa: !!formActiva,
      },
    ]);
    setIsAddModalOpen(false);
    setToast({ isOpen: true, message: 'Cocina agregada', type: 'success' });
  };

  const requestDelete = (rowId) => {
    setRowToDeleteId(rowId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (rowToDeleteId == null) return;
    setRows((prev) => prev.filter((r) => r.id !== rowToDeleteId));
    setIsDeleteModalOpen(false);
    setRowToDeleteId(null);
    setToast({ isOpen: true, message: 'Cocina eliminada', type: 'success' });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/restaurantes" className="hover:text-teal-600">
          Restaurantes
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Configuración</span>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Cocinas</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Cocinas</h1>
          <p className="text-gray-600 text-sm mt-1">
            Defina las cocinas o estaciones que operan <span className="font-medium">en simultáneo</span>. Solo las
            marcadas como activas se consideran en servicio al mismo tiempo.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="px-3 py-2 rounded-md border border-gray-200 bg-white">
            <div className="text-xs text-gray-500">Cocinas activas (simultáneas)</div>
            <div className="font-semibold text-teal-800">{totals.activas}</div>
          </div>
          <div className="px-3 py-2 rounded-md border border-gray-200 bg-white">
            <div className="text-xs text-gray-500">Total registradas</div>
            <div className="font-semibold text-gray-900">{totals.total}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-4 py-3 bg-teal-50 border-b border-gray-200">
          <h2 className="font-semibold text-teal-800">Estaciones de cocina</h2>
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="text-sm text-gray-600">
              Agregue o edite cocinas; use <span className="font-medium">Activa</span> para indicar si participa en el
              flujo simultáneo del día.
            </div>
            <button
              type="button"
              onClick={openAddModal}
              className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 shrink-0"
            >
              + Agregar cocina
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Orden</th>
                  <th className="text-left px-4 py-3 font-medium">Nombre</th>
                  <th className="text-left px-4 py-3 font-medium">Código</th>
                  <th className="text-left px-4 py-3 font-medium">Activa</th>
                  <th className="text-left px-4 py-3 font-medium w-[160px]">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-teal-700">{row.orden}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{row.nombre}</td>
                    <td className="px-4 py-3 text-gray-700">{row.codigo || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={row.activa ? 'text-green-700 font-medium' : 'text-gray-500'}>
                        {row.activa ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => openEditModal(row)}
                          className="text-sm text-teal-700 hover:text-teal-900 font-medium"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => requestDelete(row.id)}
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                          Quitar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                      No hay cocinas configuradas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isAddModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Agregar cocina</h3>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={formNombre}
                  onChange={(e) => setFormNombre(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Ej. Parrilla, Ensaladas..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código (opcional)</label>
                <input
                  type="text"
                  value={formCodigo}
                  onChange={(e) => setFormCodigo(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm uppercase"
                  placeholder="Ej. PAR"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Orden de visualización</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={formOrden}
                  onChange={(e) => setFormOrden(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formActiva} onChange={(e) => setFormActiva(e.target.checked)} />
                  <span className="text-sm text-gray-700">Activa (opera en simultáneo con las demás activas)</span>
                </label>
              </div>
              <p className="text-xs text-gray-500">
                La configuración se guarda en el navegador y define cuántos pedidos pueden estar en cocina a la vez.
              </p>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={upsertRow}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md transition-colors"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && editingRow && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Editar cocina</h3>
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingRow(null);
                }}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={formNombre}
                  onChange={(e) => setFormNombre(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código (opcional)</label>
                <input
                  type="text"
                  value={formCodigo}
                  onChange={(e) => setFormCodigo(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm uppercase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Orden de visualización</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={formOrden}
                  onChange={(e) => setFormOrden(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formActiva} onChange={(e) => setFormActiva(e.target.checked)} />
                  <span className="text-sm text-gray-700">Activa (opera en simultáneo con las demás activas)</span>
                </label>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingRow(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={upsertRow}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md transition-colors"
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setRowToDeleteId(null);
        }}
        onConfirm={confirmDelete}
        title="Eliminar cocina"
        message="¿Seguro que deseas quitar esta cocina de la configuración?"
        confirmText="Eliminar"
        cancelText="Cancelar"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Cocinas;
